/**
 * Abandoned cart recovery tracking.
 *
 * Listens for cart changes (pub/sub `cartUpdate` events), snapshots the cart
 * and syncs it to the cart-recovery-worker keyed by the shopper's WhatsApp
 * number. The worker then sends WhatsApp reminders at 3h / 1d / 2d of
 * inactivity with the shopper's discount code.
 *
 * - If the phone is already known (captured by the welcome discount popup and
 *   cached in localStorage), the shopper is enrolled silently.
 * - If not, a persistent inline capture card is shown in the cart (drawer and
 *   cart page) right above the checkout button
 *   (see snippets/cart-recovery-inline.liquid). It is hidden via the
 *   `cart-recovery-enrolled` class on <html> once the shopper is enrolled.
 * - When the cart empties (checkout or manual clear), an empty sync tells the
 *   worker to stop reminders.
 *
 * The cart drawer re-renders its DOM on every cart update, so all form
 * handlers use document-level event delegation instead of direct bindings.
 */
(() => {
	// Shared with discount-popup.js so both flows read/write the same identity.
	const PHONE_KEY = 'mellow_discount_popup_phone';
	const NAME_KEY = 'mellow_discount_popup_name';
	const PREFERENCE_KEY = 'mellow_discount_popup_preference';
	const WELCOME_POPUP_STATE_KEY = 'mellow_discount_popup_state_v2';

	const LAST_SYNC_KEY = 'mellow_cart_recovery_last_sync';

	// Added to <html> once the shopper is enrolled; CSS hides the inline card.
	const ENROLLED_CLASS = 'cart-recovery-enrolled';

	const SYNC_DEBOUNCE_MS = 5000;
	const SUCCESS_VISIBLE_MS = 6000;

	const CART_RECOVERY_WORKER_URL =
		'https://cart-recovery-worker.support-bd5.workers.dev/cart-sync';
	// Same endpoint the welcome popup uses to issue the shopper's discount code.
	const DISCOUNT_CODE_WORKER_URL =
		'https://discount-code-worker.support-bd5.workers.dev';

	let syncTimer = null;

	function safeParseJson(str) {
		try {
			return JSON.parse(str);
		} catch {
			return null;
		}
	}

	function getStored(key) {
		try {
			return window.localStorage?.getItem(key) || '';
		} catch {
			return '';
		}
	}

	function setStored(key, value) {
		try {
			window.localStorage?.setItem(key, value);
		} catch {
			// ignore
		}
	}

	function getSavedPhone() {
		const phone = getStored(PHONE_KEY);
		return /^\d{10}$/.test(phone.replace(/\D/g, ''))
			? phone.replace(/\D/g, '')
			: '';
	}

	function getSavedName() {
		return getStored(NAME_KEY).trim();
	}

	/* ------------------------------------------------------------------ *
	 * Cart syncing
	 * ------------------------------------------------------------------ */

	async function fetchCart() {
		// Relative URL on purpose: window.shopUrl points at the myshopify domain
		// even under `shopify theme dev`, which breaks with CORS on localhost.
		const res = await fetch('/cart.js', {
			headers: { Accept: 'application/json' },
		});
		if (!res.ok) throw new Error(`cart.js fetch failed: ${res.status}`);
		return res.json();
	}

	/**
	 * Stamp the shopper's number onto the cart as a hidden attribute. It flows
	 * onto the order at checkout, letting the recovery worker match orders to
	 * enrolled shoppers (custom apps can't read customer phone fields on
	 * Basic-plan stores). Idempotent: only fires when the value changed.
	 */
	function stampCartAttribute(cart, phone) {
		if (!phone || !cart || !cart.item_count) return;
		if ((cart.attributes || {})._recovery_phone === phone) return;
		fetch('/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ attributes: { _recovery_phone: phone } }),
		}).catch(() => {
			// Best effort — never break the shopping UX.
		});
	}

	function cartSignature(items, totalPrice) {
		return (
			items.map((item) => `${item.variantId}:${item.quantity}`).join(',') +
			`|${totalPrice}`
		);
	}

	async function syncCart() {
		const phone = getSavedPhone();
		if (!phone) return;

		let cart;
		try {
			cart = await fetchCart();
		} catch {
			return;
		}

		// Before the dedupe check on purpose: an unchanged cart still needs the
		// attribute if the shopper enrolled after building it.
		stampCartAttribute(cart, phone);

		const items = (cart.items || []).map((item) => ({
			variantId: item.variant_id,
			quantity: item.quantity,
			title: item.product_title,
			price: item.price,
		}));

		const signature = cartSignature(items, cart.total_price || 0);
		const lastSync = safeParseJson(getStored(LAST_SYNC_KEY));
		if (lastSync && lastSync.signature === signature) return;

		// Nothing was ever synced and the cart is empty: nothing to recover,
		// so skip the request and just remember the state.
		if (!lastSync && items.length === 0) {
			setStored(
				LAST_SYNC_KEY,
				JSON.stringify({ signature, itemCount: 0, at: Date.now() })
			);
			return;
		}

		try {
			const res = await fetch(CART_RECOVERY_WORKER_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					phone,
					name: getSavedName(),
					items,
					cartTotal: cart.total_price || 0,
				}),
			});
			if (res.ok) {
				setStored(
					LAST_SYNC_KEY,
					JSON.stringify({ signature, itemCount: items.length, at: Date.now() })
				);
			}
		} catch {
			// Silently fail — recovery tracking must never break the shopping UX.
		}
	}

	function scheduleSync() {
		if (!getSavedPhone()) return;
		if (syncTimer) window.clearTimeout(syncTimer);
		syncTimer = window.setTimeout(() => {
			syncTimer = null;
			syncCart();
		}, SYNC_DEBOUNCE_MS);
	}

	/* ------------------------------------------------------------------ *
	 * Inline capture card (rendered in the cart, above the checkout button)
	 * ------------------------------------------------------------------ */

	function markWelcomePopupClaimed() {
		// Prevent the full-screen welcome popup from re-asking for the number.
		setStored(
			WELCOME_POPUP_STATE_KEY,
			JSON.stringify({ claimed: true, claimedAt: Date.now() })
		);
	}

	function setEnrolled() {
		document.documentElement.classList.add(ENROLLED_CLASS);
	}

	function isValidName(value) {
		return String(value || '').trim().length >= 2;
	}

	function isValidPhone(value) {
		return String(value || '').replace(/\D/g, '').length === 10;
	}

	function updateValidationUi(card) {
		const nameInput = card.querySelector('[data-cart-recovery-name]');
		const phoneInput = card.querySelector('[data-cart-recovery-phone]');
		const submitBtn = card.querySelector('[data-cart-recovery-submit]');
		const statusEl = card.querySelector('[data-cart-recovery-status]');
		if (submitBtn) {
			submitBtn.disabled = !(
				isValidName(nameInput?.value) && isValidPhone(phoneInput?.value)
			);
		}
		if (statusEl) statusEl.textContent = '';
	}

	function handleSubmit(card) {
		const nameInput = card.querySelector('[data-cart-recovery-name]');
		const phoneInput = card.querySelector('[data-cart-recovery-phone]');
		const submitBtn = card.querySelector('[data-cart-recovery-submit]');
		const statusEl = card.querySelector('[data-cart-recovery-status]');

		const nameValue = String(nameInput?.value || '').trim();
		const phoneValue = String(phoneInput?.value || '').replace(/\D/g, '');

		if (!isValidName(nameValue) || !isValidPhone(phoneValue)) {
			if (statusEl) {
				statusEl.textContent =
					'Please enter your name and a valid 10-digit phone number.';
			}
			return;
		}

		if (submitBtn) {
			submitBtn.disabled = true;
			submitBtn.textContent = 'Sending...';
		}

		// Issue the discount code through the same flow as the welcome popup.
		// `source` identifies the capture surface (the popup sends 'popup'),
		// so analytics can tell inline-cart claims apart from popup claims.
		const preference = getStored(PREFERENCE_KEY) || 'cart-recovery';
		fetch(DISCOUNT_CODE_WORKER_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				preference,
				phone: phoneValue,
				name: nameValue,
				source: 'cart-inline',
			}),
		})
			.then((res) => res.json().catch(() => null))
			.then((result) => {
				const claimed =
					result && (result.ok || result.error === 'already_claimed');

				if (!claimed) {
					if (statusEl) {
						statusEl.textContent =
							"We couldn't send your code right now. Please try again in a moment.";
					}
					if (submitBtn) {
						submitBtn.disabled = false;
						submitBtn.textContent = 'Get discount code';
					}
					return;
				}

				// Save identity so future carts auto-enroll silently.
				setStored(NAME_KEY, nameValue);
				setStored(PHONE_KEY, phoneValue);
				markWelcomePopupClaimed();

				const success = card.querySelector('[data-cart-recovery-success]');
				const formStep = card.querySelector('[data-cart-recovery-form-step]');
				if (formStep) formStep.classList.add('cart-recovery-inline__step--hidden');
				if (success) success.classList.remove('cart-recovery-inline__step--hidden');

				// Enroll the current cart right away.
				syncCart();

				// Keep the success message readable for a moment, then hide the
				// card everywhere (drawer + cart page) for good.
				window.setTimeout(setEnrolled, SUCCESS_VISIBLE_MS);
			})
			.catch(() => {
				if (statusEl) {
					statusEl.textContent =
						"We couldn't send your code right now. Please try again in a moment.";
				}
				if (submitBtn) {
					submitBtn.disabled = false;
					submitBtn.textContent = 'Get discount code';
				}
			});
	}

	function initInlineCapture() {
		if (getSavedPhone()) {
			setEnrolled();
			return;
		}

		// Welcome popup captured the identity on this same page: hide the
		// inline card immediately and enroll the current cart.
		document.addEventListener('mellow:identity-captured', () => {
			if (!getSavedPhone()) return;
			setEnrolled();
			syncCart();
		});

		// The cart drawer re-renders on every cart update, so bind at the
		// document level instead of on the (replaceable) card elements.
		document.addEventListener('input', (e) => {
			const card =
				e.target instanceof Element &&
				e.target.closest('[data-cart-recovery-inline]');
			if (card) updateValidationUi(card);
		});

		document.addEventListener('submit', (e) => {
			const form =
				e.target instanceof Element &&
				e.target.closest('[data-cart-recovery-form]');
			if (!form) return;
			e.preventDefault();
			const card = form.closest('[data-cart-recovery-inline]');
			if (card) handleSubmit(card);
		});
	}

	/* ------------------------------------------------------------------ *
	 * Wiring
	 * ------------------------------------------------------------------ */

	function init() {
		if (window.Shopify?.designMode) return;

		initInlineCapture();

		// React to cart changes published by product-form.js / cart.js.
		if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
				if (getSavedPhone()) {
					// Covers enrollment via the welcome popup mid-session too.
					setEnrolled();
					scheduleSync();
				}
			});
			if (PUB_SUB_EVENTS.quantityUpdate) {
				subscribe(PUB_SUB_EVENTS.quantityUpdate, () => scheduleSync());
			}
		}

		// On page load, re-sync if the cart changed since our last snapshot.
		// Covers: carts emptied by checkout, edits from other tabs, and users
		// who gave their number (welcome popup) after already filling a cart.
		if (getSavedPhone()) {
			window.setTimeout(syncCart, 3000);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
